import React, { Fragment, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { promotionApi } from '@app/store';
import { IPromotion, PromotionDTO } from '@app/common';
import { Button, CardButtonActionMode, Form, Panel } from '@app/components';
import { PromotionForm } from './PromotionForm';
import { PromotionFormValues, transformStepFormValuesToPayloadPromotion, transformToUpdatableFormPromotion } from './promotion-form.helpers';
import { useToast, useTriptych } from '@app/hooks';
import { PromotionCard } from './PromotionCard';
import { InputFileRef } from '@app/components/form/InputFile';
import { CheckPermissions } from '@app/components/CheckPermissions';
import { valeursDeCopieDeLaPromotion } from '../Duplication/valeurs-de-copie';

interface PromotionUpdateProps {
  promotion?: Partial<IPromotion>;
  isFetching?: boolean;
  onDelete: (product: Partial<IPromotion>) => void;
  toggle: (mode: CardButtonActionMode) => void;
}

export const PromotionUpdate = ({ promotion, isFetching, onDelete, toggle }: PromotionUpdateProps) => {
  const toast = useToast();

  const triptych = useTriptych();

  const cardImageRef = useRef<InputFileRef>();

  const [update, { isLoading, error: apiErrors }] = promotionApi.useUpdateOnePromotionsMutation();

  const formMethods = useForm<PromotionFormValues>({
    resolver: classValidatorResolver(PromotionDTO),
    defaultValues: transformToUpdatableFormPromotion(promotion),
  });

  useEffect(() => {
    formMethods.reset(transformToUpdatableFormPromotion(promotion));
  }, [promotion, formMethods.reset]);

  const onUpdate = async (formValues: PromotionFormValues) => {
    try {
      const updatedPromotion = await update({ _id: promotion?._id!, formData: transformStepFormValuesToPayloadPromotion(formValues) }).unwrap();

      triptych.close({ mode: 'UPDATE', data: updatedPromotion });

      toast.success('Modification effectuée avec succès.')
    } catch (error) {
      console.error(error);
      toast.error('Une erreur est survenue lors de la modification.')
    }
  }

  return (
    <Fragment>
      <PromotionCard
        promotion={promotion}
        formMethods={formMethods}
        toggle={toggle}
        onOpenImagePicker={() => cardImageRef.current?.open()} />

      <Panel.Content>

        <Form isLoading={isFetching} formMethods={formMethods} apiErrors={apiErrors}>

          <Panel.FormContainer>

            <Panel.Header>
              <CheckPermissions permissions={['dashboard::promotions.create']}>
                <Button
                  title="Dupliquer"
                  disabled={!promotion || isFetching}
                  bordered
                  onPress={() => triptych.open({ mode: 'CREATE', data: valeursDeCopieDeLaPromotion(promotion as IPromotion) })} />
              </CheckPermissions>

              <CheckPermissions permissions={['dashboard::promotions.delete']}>
                <Button
                  title="Supprimer"
                  disabled={!promotion}
                  onPress={() => onDelete(promotion!)}
                  bordered
                  important />
              </CheckPermissions>
            </Panel.Header>

            <PromotionForm ref={cardImageRef} />

          </Panel.FormContainer>

          <Panel.Actions>
            <Button
              title="Retour"
              grow
              large
              bordered
              onPress={() => triptych.close()} />

            <Button
              loading={isLoading}
              onPress={formMethods.handleSubmit(onUpdate)}
              title="Valider"
              grow
              large />
          </Panel.Actions>

        </Form>

      </Panel.Content>
    </Fragment>
  );
};
